// Safari sometimes reports an empty MIME type for HEIC/HEIF files, so the
// extension is checked as a fallback alongside file.type.
export function isHeicFile(file: File): boolean {
  const type = file.type.toLowerCase()
  if (type === "image/heic" || type === "image/heif") return true
  return /\.(heic|heif)$/i.test(file.name)
}

export class HeicConversionError extends Error {}

// Converts a HEIC/HEIF file to JPEG client-side (no backend/ImageMagick
// involvement - production's ImageMagick build doesn't decode HEIC, which
// silently drops the photo or 500s unrelated requests). Non-HEIC files pass
// through unchanged.
//
// Throws HeicConversionError on failure rather than silently returning the
// original HEIC file - callers must catch this and tell the user, since a
// raw HEIC file can't be previewed/cropped in non-Safari browsers either,
// so silently proceeding just produces a confusing empty result later.
//
// heic-to is ~3-4MB minified (bundles a WASM libheif decoder), so it's
// dynamically imported here rather than at module load time - only users
// who actually attach a HEIC file pay for that chunk. Using heic-to (not
// heic2any) because it tracks current libheif releases; heic2any is stale
// and fails to parse HEIC files from newer iPhones/iOS versions.
export async function convertHeicToJpeg(file: File): Promise<File> {
  if (!isHeicFile(file)) return file

  try {
    // @ts-ignore - tsc's `module: "es6"` setting doesn't recognize dynamic
    // import(); webpack/babel handle it fine at runtime regardless.
    const { heicTo } = await import("heic-to")
    const blob = await heicTo({
      blob: file,
      type: "image/jpeg",
      quality: 0.9,
    })
    const newName = file.name.replace(/\.(heic|heif)$/i, ".jpg")
    return new File([blob], newName, { type: "image/jpeg" })
  } catch (e) {
    console.warn("HEIC conversion failed:", e)
    throw new HeicConversionError(
      "This HEIC photo couldn't be converted. Try a different photo, or export it as JPEG first.",
    )
  }
}
